// Team data - 6 Attorneys and 6 Paralegals with unique names
// Using icons instead of initials for visual representation
import { 
  Building2, 
  Stamp, 
  Landmark, 
  Scale, 
  Globe, 
  Heart,
  FileText,
  Copyright,
  Briefcase,
  Users,
  Shield,
  FileCheck
} from 'lucide-react';

export const attorneys = [
  {
    id: 1,
    name: 'Marcus J. Wellington',
    initials: 'MJW',
    title: 'Managing Partner',
    specialization: 'Corporate Law & M&A',
    icon: Building2,
    bio: 'Marcus leads the firm\'s corporate practice with extensive experience in complex business transactions and mergers. He has advised Fortune 500 companies and emerging startups alike on high-stakes acquisitions, divestitures, and restructurings. His strategic approach combines deep legal analysis with practical business judgment to help clients navigate transformative transactions.',
    education: 'J.D., Harvard Law School',
    barAdmissions: ['California', 'New York', 'Massachusetts'],
    expertise: ['Mergers & Acquisitions', 'Corporate Governance', 'Securities Law'],
    languages: ['English'],
    memberships: ['American Bar Association', 'California State Bar Corporate Law Section'],
    focusAreas: 'Cross-border M&A, private equity transactions, and corporate restructurings for mid-market and Fortune 500 companies.'
  },
  {
    id: 2,
    name: 'Victoria R. Chen',
    initials: 'VRC',
    title: 'Senior Partner',
    specialization: 'Intellectual Property',
    icon: Stamp,
    bio: 'Victoria specializes in trademark and copyright law, helping clients protect their most valuable intellectual assets. She has successfully litigated high-profile IP disputes and managed large trademark portfolios for global brands. Victoria also advises technology companies on IP strategy, licensing, and brand protection in digital markets.',
    education: 'J.D., Stanford Law School',
    barAdmissions: ['California', 'Federal Circuit'],
    expertise: ['Trademark Registration', 'Copyright Law', 'IP Litigation'],
    languages: ['English'],
    memberships: ['International Trademark Association (INTA)', 'American Intellectual Property Law Association'],
    focusAreas: 'Brand protection, trademark portfolio management, and IP enforcement for technology, consumer goods, and entertainment companies.'
  },
  {
    id: 3,
    name: 'Alexander K. Blackwood',
    initials: 'AKB',
    title: 'Partner',
    specialization: 'Government Contracting',
    icon: Landmark,
    bio: 'Alexander brings extensive experience in federal procurement law, helping contractors navigate complex regulatory requirements and win government business. He represents clients before the GAO, COFC, and various agency boards of contract appeals. Alexander also counsels companies on compliance program development and small business certification strategies.',
    education: 'J.D., Georgetown University Law Center',
    barAdmissions: ['California', 'District of Columbia', 'Virginia'],
    expertise: ['Federal Contracting', 'Bid Protests', 'Compliance Programs'],
    languages: ['English'],
    memberships: ['National Contract Management Association', 'ABA Section of Public Contract Law'],
    focusAreas: 'Federal procurement, bid protests, contract disputes, small business certifications (8(a), HUBZone, SDVOSB), and cybersecurity compliance.'
  },
  {
    id: 4,
    name: 'Sophia L. Martinez',
    initials: 'SLM',
    title: 'Partner',
    specialization: 'Business Litigation',
    icon: Scale,
    bio: 'Sophia is a skilled litigator who resolves complex commercial disputes through negotiation, mediation, and when necessary, aggressive courtroom advocacy. She has tried cases in both state and federal courts, representing clients in breach of contract, partnership disputes, trade secret misappropriation, and unfair business practices claims. Her approach prioritizes practical outcomes that protect client relationships and bottom lines.',
    education: 'J.D., UC Berkeley School of Law',
    barAdmissions: ['California', 'Nevada'],
    expertise: ['Commercial Litigation', 'Contract Disputes', 'Alternative Dispute Resolution'],
    languages: ['English'],
    memberships: ['Federal Bar Association', 'California Lawyers Association Litigation Section'],
    focusAreas: 'Complex commercial litigation, partnership and shareholder disputes, trade secret protection, and mediation/arbitration advocacy.'
  },
  {
    id: 5,
    name: 'Jonathan D. Pierce',
    initials: 'JDP',
    title: 'Partner',
    specialization: 'Immigration Law',
    icon: Globe,
    bio: 'Jonathan helps businesses build global workforces through strategic immigration solutions. He has handled thousands of visa petitions and green card applications for companies ranging from tech startups to multinational corporations. Jonathan is known for creative case strategies that address even the most complex immigration challenges, including RFE responses and appeals.',
    education: 'J.D., NYU School of Law',
    barAdmissions: ['California', 'New York'],
    expertise: ['Business Immigration', 'Work Visas', 'Green Card Processing'],
    languages: ['English'],
    memberships: ['American Immigration Lawyers Association (AILA)', 'Federal Bar Association Immigration Section'],
    focusAreas: 'H-1B specialty occupation visas, L-1 intracompany transfers, O-1 extraordinary ability petitions, PERM labor certifications, and I-9 compliance audits.'
  },
  {
    id: 6,
    name: 'Eleanor H. Thornton',
    initials: 'EHT',
    title: 'Of Counsel',
    specialization: 'Non-Profit Law',
    icon: Heart,
    bio: 'Eleanor dedicates her practice to serving non-profit organizations and foundations, helping them achieve their charitable missions while maintaining compliance. She guides organizations through formation, 501(c)(3) applications, governance best practices, and ongoing regulatory requirements. Eleanor also advises on donor-advised funds, charitable trusts, and philanthropic planning.',
    education: 'J.D., Yale Law School',
    barAdmissions: ['California', 'Connecticut'],
    expertise: ['Non-Profit Formation', 'Tax-Exempt Status', 'Foundation Law'],
    languages: ['English'],
    memberships: ['National Council of Nonprofits', 'ABA Section of Taxation (Exempt Organizations Committee)'],
    focusAreas: 'Non-profit formation and governance, 501(c)(3) applications, charitable solicitation compliance, and foundation grant-making programs.'
  }
];

export const paralegals = [
  {
    id: 7,
    name: 'Rachel N. Foster',
    initials: 'RNF',
    title: 'Senior Paralegal',
    specialization: 'Corporate Transactions',
    icon: FileText,
    bio: 'Rachel provides critical support for complex M&A transactions and corporate formations. Her attention to detail ensures smooth closings and compliant documentation. She manages due diligence processes, coordinates closing checklists, and maintains corporate minute books for clients across multiple jurisdictions.',
    experience: 'Corporate law specialist with deep expertise in transaction management',
    certifications: ['Certified Paralegal (NALA)', 'Notary Public'],
    skills: ['Due Diligence Coordination', 'Entity Formation Filings', 'Corporate Records Management']
  },
  {
    id: 8,
    name: 'Daniel M. O\'Brien',
    initials: 'DMO',
    title: 'Senior Paralegal',
    specialization: 'Intellectual Property',
    icon: Copyright,
    bio: 'Daniel manages trademark portfolios and coordinates patent filings for clients across multiple industries. He excels at deadline management and client communication. His expertise includes conducting trademark clearance searches, preparing USPTO filings, and monitoring trademark databases for potential infringement.',
    experience: 'Seasoned IP paralegal with extensive USPTO filing experience',
    certifications: ['Certified Paralegal (NALA)', 'USPTO Registered'],
    skills: ['Trademark Clearance Searches', 'USPTO Portfolio Management', 'IP Docketing Systems']
  },
  {
    id: 9,
    name: 'Amelia C. Hayes',
    initials: 'ACH',
    title: 'Paralegal',
    specialization: 'Litigation Support',
    icon: Briefcase,
    bio: 'Amelia provides comprehensive litigation support including discovery management, deposition coordination, and trial preparation for complex commercial disputes. She is proficient with e-discovery platforms and manages document review processes to ensure thorough and cost-effective case preparation.',
    experience: 'Litigation support professional skilled in e-discovery and trial prep',
    certifications: ['Certified Paralegal (NALA)'],
    skills: ['E-Discovery Management', 'Deposition Coordination', 'Trial Preparation']
  },
  {
    id: 10,
    name: 'Benjamin T. Reyes',
    initials: 'BTR',
    title: 'Paralegal',
    specialization: 'Immigration',
    icon: Users,
    bio: 'Benjamin assists with visa petitions, green card applications, and I-9 compliance. His multilingual abilities help serve diverse client needs effectively. He prepares detailed petition packages, tracks case progress with USCIS, and assists employers with workforce immigration planning and compliance audits.',
    experience: 'Immigration paralegal with strong case management skills',
    certifications: ['Certified Paralegal (NFPA)'],
    skills: ['Visa Petition Preparation', 'USCIS Case Tracking', 'I-9 Audit Support']
  },
  {
    id: 11,
    name: 'Catherine E. Walsh',
    initials: 'CEW',
    title: 'Paralegal',
    specialization: 'Government Contracting',
    icon: Shield,
    bio: 'Catherine supports government contract compliance, proposal preparation, and certification applications. She has deep knowledge of FAR and DFARS requirements. She assists with SAM registrations, GSA schedule applications, and helps contractors maintain compliance across multiple agency engagements.',
    experience: 'Government contracting compliance specialist',
    certifications: ['Certified Paralegal (NALA)', 'Security Clearance'],
    skills: ['FAR/DFARS Compliance', 'Proposal Preparation', 'SAM Registration']
  },
  {
    id: 12,
    name: 'Michael K. Patterson',
    initials: 'MKP',
    title: 'Paralegal',
    specialization: 'Contract Administration',
    icon: FileCheck,
    bio: 'Michael handles contract drafting, review, and administration across practice areas. His systematic approach ensures consistent quality and timely delivery. He maintains contract databases, tracks key dates and renewal deadlines, and assists attorneys with redlining and version control for complex multi-party agreements.',
    experience: 'Contract administration specialist across multiple practice areas',
    certifications: ['Certified Paralegal (NALA)'],
    skills: ['Contract Drafting & Review', 'Deadline & Renewal Tracking', 'Document Version Control']
  }
];

export const allTeamMembers = [...attorneys, ...paralegals];
